import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Pause, Play, X, AlertTriangle, ChevronDown, ChevronUp, ArrowLeft,
  CheckCircle, Eye, Send as SendIcon, Ban,
} from 'lucide-react';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { CampaignDeliveryChart } from '@/components/campaigns/CampaignDeliveryChart';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  whatsappBroadcastsService,
  Broadcast,
  BroadcastRecipient,
  BroadcastStatus,
} from '@/services/whatsapp-broadcasts.service';

const POLL_MS = 5000;

const STATUS_TONE: Record<BroadcastStatus, string> = {
  DRAFT:             'bg-surface-alt text-text-muted',
  PREFLIGHT_BLOCKED: 'bg-danger-soft text-danger',
  SCHEDULED:         'bg-info-soft text-info',
  RUNNING:           'bg-success-soft text-success',
  PAUSED:            'bg-warning-soft text-warning',
  COMPLETED:         'bg-info-soft text-info',
  CANCELLED:         'bg-surface-alt text-text-muted',
  FAILED:            'bg-danger-soft text-danger',
};

const KNOWN_BREAKER_REASONS: Record<string, string> = {
  'failure-rate-breaker': 'Auto-paused: failure rate exceeded 15% over the last 100 sends. Investigate the failing recipients before resuming.',
  'block-rate-breaker':   'Auto-paused: block-rate proxy exceeded 2% over the last 500 sends (STOP keywords or 132010 errors). Investigate template + audience quality before resuming.',
  'quality-RED':          'Auto-paused: WhatsApp quality rating dropped to RED. Pause all marketing on this number for at least 24h, then re-evaluate.',
  'template-paused':      'Auto-paused: Meta paused the template used by this broadcast. Pick a different template or wait for Meta to unpause it.',
};
function isBreaker(reason?: string) {
  if (!reason) return false;
  return reason in KNOWN_BREAKER_REASONS || reason.startsWith('tier-downgraded');
}

function CounterCard({ label, value, icon, tone }: { label: string; value: number; icon: React.ReactNode; tone?: string }) {
  return (
    <Card padded>
      <div className="flex items-center justify-between">
        <span className="text-2xs font-medium uppercase tracking-wider text-text-muted">{label}</span>
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${tone ?? 'bg-surface-alt text-text-muted'}`}>
          {icon}
        </span>
      </div>
      <div className="mt-1.5 text-xl font-semibold text-text-primary tabular-nums">{value.toLocaleString()}</div>
    </Card>
  );
}

export default function BroadcastDetail() {
  const { broadcastUid } = useParams<{ broadcastUid: string }>();
  const [b, setB] = useState<Broadcast | null>(null);
  const [confirm, setConfirm] = useState<'pause' | 'resume' | 'cancel' | null>(null);
  const [loading, setLoading] = useState(true);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const [recipients, setRecipients] = useState<BroadcastRecipient[] | null>(null);
  const [recipientFilter, setRecipientFilter] = useState<string | undefined>(undefined);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useCallback(async () => {
    if (!broadcastUid) return;
    try {
      const fresh = await whatsappBroadcastsService.get(broadcastUid);
      setB(fresh);
    } catch {
      // 404 etc — interceptor will surface
    } finally {
      setLoading(false);
    }
  }, [broadcastUid]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live poll while RUNNING — stops on terminal/paused states.
  useEffect(() => {
    if (!b) return;
    const isLive = b.status === 'RUNNING' || b.status === 'SCHEDULED';
    if (pollRef.current) clearInterval(pollRef.current);
    if (isLive) {
      pollRef.current = setInterval(refresh, POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [b, refresh]);

  const fetchRecipients = useCallback(
    async (status?: string) => {
      if (!broadcastUid) return;
      const r = await whatsappBroadcastsService.recipients(broadcastUid, status, 1, 100);
      setRecipients(r.items);
    },
    [broadcastUid],
  );

  useEffect(() => {
    if (recipientsExpanded) fetchRecipients(recipientFilter);
  }, [recipientsExpanded, recipientFilter, fetchRecipients]);

  const handleAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!broadcastUid) return;
    setConfirm(null);
    try {
      if (action === 'pause') await whatsappBroadcastsService.pause(broadcastUid);
      if (action === 'resume') await whatsappBroadcastsService.resume(broadcastUid);
      if (action === 'cancel') await whatsappBroadcastsService.cancel(broadcastUid);
      toast.success(`Broadcast ${action === 'cancel' ? 'cancelled' : action + 'd'}`);
      refresh();
    } catch {
      // interceptor surfaces
    }
  };

  if (loading || !b) {
    return (
      <div>
        <WhatsAppHeader section="campaigns" title="Broadcast" />
        <div className="px-7 py-12 text-center text-sm text-text-muted">Loading…</div>
      </div>
    );
  }

  const queued = b.queued ?? Math.max(0, b.totalRecipients - b.sent - b.failed - b.skipped);
  const breakerBannerText = b.pauseReason && KNOWN_BREAKER_REASONS[b.pauseReason]
    ? KNOWN_BREAKER_REASONS[b.pauseReason]
    : b.pauseReason?.startsWith('tier-downgraded')
      ? `Auto-paused: messaging tier downgraded (${b.pauseReason.replace('tier-downgraded:', '')}). Wait for tier to recover before resuming.`
      : null;

  return (
    <div>
      <WhatsAppHeader section="campaigns"
        title={b.name}
        description={`Broadcast · ${b.broadcastUid}`}
        actions={
          <div className="flex items-center gap-2">
            {b.status === 'RUNNING' && (
              <Button variant="secondary" iconLeft={<Pause size={14} />} onClick={() => setConfirm('pause')}>
                Pause
              </Button>
            )}
            {b.status === 'PAUSED' && (
              <Button iconLeft={<Play size={14} />} onClick={() => setConfirm('resume')}>
                Resume
              </Button>
            )}
            {(b.status === 'RUNNING' || b.status === 'PAUSED' || b.status === 'SCHEDULED' || b.status === 'DRAFT' || b.status === 'PREFLIGHT_BLOCKED') && (
              <Button variant="ghost" iconLeft={<X size={14} />} onClick={() => setConfirm('cancel')}>
                Cancel
              </Button>
            )}
          </div>
        }
      />

      <div className="px-7 py-5 space-y-5">
        <Link to="/whatsapp-account/bulk" className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary">
          <ArrowLeft size={12} /> All broadcasts
        </Link>

        {/* Breaker banner (Plan F Task 5) */}
        {b.status === 'PAUSED' && isBreaker(b.pauseReason) && breakerBannerText && (
          <Card padded className="border-warning bg-warning-soft/40">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="mt-0.5 text-warning" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-text-primary">
                  Paused by safety system: {b.pauseReason}
                </div>
                <p className="mt-0.5 text-xs text-text-secondary">{breakerBannerText}</p>
                <Link
                  to="/whatsapp-account/audit"
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  Why was this paused? →
                </Link>
              </div>
            </div>
          </Card>
        )}

        {/* Header info */}
        <Card padded>
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-2xs font-semibold ${STATUS_TONE[b.status]}`}>
                {b.status.toLowerCase().replace('_', ' ')}
              </span>
              {b.messageMode === 'custom' ? (
                <div className="mt-2 space-y-1.5">
                  {b.customMessage && (
                    <div className="max-w-md rounded-lg border border-border bg-surface-alt/50 px-3 py-2 text-xs text-text-secondary whitespace-pre-wrap break-words">
                      {b.customMessage}
                    </div>
                  )}
                  {b.customMediaUrl && (
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      {b.customMediaType === 'image' ? (
                        <img src={b.customMediaUrl} alt="" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <span className="rounded bg-surface-alt px-1.5 py-0.5 text-2xs uppercase text-text-secondary">{b.customMediaType}</span>
                      )}
                      <a href={b.customMediaUrl} target="_blank" rel="noreferrer" className="text-primary underline truncate max-w-[260px]">
                        {b.customMediaFilename ?? 'view attachment'}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2 text-xs text-text-muted">
                  Template: <span className="font-mono text-text-secondary">{b.templateUid}</span> · Language: {b.templateLanguage}
                </div>
              )}
              {b.scheduledAt && (
                <div className="text-xs text-text-muted">
                  Scheduled for {new Date(b.scheduledAt).toLocaleString()}
                </div>
              )}
              {b.startedAt && (
                <div className="text-xs text-text-muted">
                  Started {new Date(b.startedAt).toLocaleString()}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xs font-medium uppercase tracking-wider text-text-muted">Progress</div>
              <div className="text-2xl font-semibold tabular-nums text-text-primary">
                {b.sent.toLocaleString()} / {b.totalRecipients.toLocaleString()}
              </div>
              <div className="mt-1 h-2 w-48 overflow-hidden rounded-full bg-surface-alt">
                <div
                  className="h-full rounded-full bg-gold-gradient transition-all duration-500"
                  style={{ width: `${b.totalRecipients > 0 ? Math.min(100, (b.sent / b.totalRecipients) * 100) : 0}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

{b.pauseReason && b.status === 'PAUSED' && !isBreaker(b.pauseReason) && <div className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">{b.pauseReason}</div>}
        {!!b.unknown && <div className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">{b.unknown} messages have unconfirmed delivery. They will not be resent automatically; review the recipient details and actual conversation first.</div>}
        {!!b.deferred && <p className="text-sm text-text-muted">{b.deferred} recipients are waiting for pacing, a retry, or rolling allowance. Their next attempt is shown below.</p>}
<CampaignDeliveryChart sent={b.sent} delivered={b.delivered} read={b.readCount} failed={b.failed} />

        {/* Counter grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <CounterCard label="Queued" value={queued} icon={<SendIcon size={12} />} />
          <CounterCard label="Sent" value={b.sent} icon={<SendIcon size={12} />} tone="bg-info-soft text-info" />
          <CounterCard label="Delivered" value={b.delivered} icon={<CheckCircle size={12} />} tone="bg-info-soft text-info" />
          <CounterCard label="Read" value={b.readCount} icon={<Eye size={12} />} tone="bg-success-soft text-success" />
          <CounterCard label="Failed" value={b.failed} icon={<X size={12} />} tone="bg-danger-soft text-danger" />
          <CounterCard label="Skipped" value={b.skipped} icon={<Ban size={12} />} tone="bg-warning-soft text-warning" />
        </div>

        {/* Hygiene rollup */}
        <Card padded>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Audience hygiene</h3>
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <div>
              <div className="text-text-muted">Submitted</div>
              <div className="font-semibold text-text-primary">{b.totalSubmitted.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-text-muted">Duplicates removed</div>
              <div className="font-semibold text-text-primary">{b.strippedDuplicateCount.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-text-muted">Opt-outs removed</div>
              <div className="font-semibold text-text-primary">{b.strippedOptOutCount.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-text-muted">Invalid format removed</div>
              <div className="font-semibold text-text-primary">{b.strippedInvalidCount.toLocaleString()}</div>
            </div>
          </div>
        </Card>

        {/* Recipient drill-down */}
        <Card padded>
          <button
            type="button"
            className="flex w-full items-center justify-between text-sm font-semibold text-text-primary"
            onClick={() => setRecipientsExpanded((v) => !v)}
          >
            <span>Per-recipient details</span>
            {recipientsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {recipientsExpanded && (
            <div className="mt-4">
              <div className="mb-3 flex flex-wrap gap-2">
                {[
                  { v: undefined, l: 'All' },
                  { v: 'QUEUED', l: 'Queued' },
                  { v: 'SENT', l: 'Sent' },
                  { v: 'DELIVERED', l: 'Delivered' },
                  { v: 'READ', l: 'Read' },
                  { v: 'FAILED', l: 'Failed' },
                  { v: 'UNKNOWN', l: 'Unconfirmed' },
                  { v: 'SENDING', l: 'Sending' },
                  { v: 'SKIPPED', l: 'Skipped' },
                ].map((f) => (
                  <button
                    key={f.l}
                    type="button"
                    onClick={() => setRecipientFilter(f.v)}
                    className={`rounded-full border px-3 py-1 text-2xs font-medium transition-colors ${
                      recipientFilter === f.v
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border text-text-muted hover:border-border-strong'
                    }`}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
              {recipients === null ? (
                <div className="py-4 text-center text-xs text-text-muted">Loading…</div>
              ) : recipients.length === 0 ? (
                <div className="py-4 text-center text-xs text-text-muted">No recipients in this filter.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-2xs font-semibold uppercase tracking-wider text-text-muted">
                        <th className="px-3 py-2">Phone</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Sent at</th>
                        <th className="px-3 py-2">Delivery details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((r) => (
                        <tr key={r.recipientUid} className="border-b border-border/50 last:border-0">
                          <td className="px-3 py-1.5 font-mono text-text-primary">{r.phoneE164}</td>
                          <td className="px-3 py-1.5 text-text-secondary">{r.status ?? '—'}</td>
                          <td className="px-3 py-1.5 text-2xs text-text-muted">
                            {r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-2xs text-text-secondary">{r.failureReason ?? r.deferReason ?? '—'}{r.nextAttemptAt && r.status === 'QUEUED' && <div className="mt-1 text-text-muted">Next attempt: {new Date(r.nextAttemptAt).toLocaleString()}</div>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-2xs text-text-muted">Showing first 100 in this filter.</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={confirm === 'pause'}
        title="Pause this broadcast?"
        message="Pausing stops dispatch. Already-queued messages keep their state. You can resume any time, and it will re-run safety checks first."
        onConfirm={() => handleAction('pause')}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'resume'}
        title="Resume this broadcast?"
        message="The system will re-check the breakers and tier headroom before re-arming the worker."
        onConfirm={() => handleAction('resume')}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        title="Cancel this broadcast?"
        message="Cancelled broadcasts cannot be resumed. Already-sent messages stay sent; queued ones never go out."
        danger
        onConfirm={() => handleAction('cancel')}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
